import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Send, Phone, Mail, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const subjects = [
  { value: 'order', label: 'Order Question' },
  { value: 'wholesale', label: 'Wholesale/Team Order' },
  { value: 'collab', label: 'Collab/Partnership' },
  { value: 'other', label: 'Other' },
];

const Contact = () => {
  useEffect(() => { document.title = 'Contact | Crookid Smilez'; }, []);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: 'order', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Message sent!');
      } else {
        toast.error('Error sending message');
      }
    } catch (error) {
      toast.error('Error sending message');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-cs-bg border border-cs-border text-cs-text font-mono text-sm p-3 focus:outline-none focus:border-cs-ochre transition-colors placeholder:text-cs-muted/50";
  const labelClass = "font-mono text-sm text-cs-text uppercase tracking-wider block mb-2";

  return (
    <div className="fade-in" data-testid="contact-page">
      {/* Header */}
      <section className="bg-cs-dark border-b border-cs-border py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs text-cs-ochre uppercase tracking-[0.3em] mb-3">Get In Touch</p>
          <h1 className="font-graffiti text-5xl md:text-7xl uppercase text-cs-text leading-none" data-testid="contact-title">
            Contact
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-cs-card border border-cs-border p-6" data-testid="contact-email-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-cs-ochre flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-cs-dark" />
                </div>
                <h3 className="font-mono text-xs uppercase tracking-widest text-cs-ochre">Email</h3>
              </div>
              <a href="mailto:info@crookidsmilez.com" className="font-mono text-sm text-cs-text hover:text-cs-ochre transition-colors" data-testid="contact-email">
                info@crookidsmilez.com
              </a>
            </div>

            <div className="bg-cs-card border border-cs-border p-6" data-testid="contact-phone-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-cs-ochre flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-cs-dark" />
                </div>
                <h3 className="font-mono text-xs uppercase tracking-widest text-cs-ochre">Phone</h3>
              </div>
              <a href="tel:+13177594033" className="font-mono text-sm text-cs-text hover:text-cs-ochre transition-colors" data-testid="contact-phone">
                (317) 759-4033
              </a>
            </div>

            <div className="bg-cs-card border border-cs-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-cs-ochre flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-cs-dark" />
                </div>
                <h3 className="font-mono text-xs uppercase tracking-widest text-cs-ochre">Location</h3>
              </div>
              <p className="font-mono text-sm text-cs-text">Indianapolis, IN</p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="bg-cs-card border border-cs-olive p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-cs-olive flex items-center justify-center">
                  <Send className="w-8 h-8 text-cs-text" />
                </div>
                <h2 className="font-anton text-2xl uppercase text-cs-text mb-4">Message Sent</h2>
                <p className="font-mono text-sm text-cs-muted mb-6">We'll get back to you soon.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: 'order', message: '' }); }} className="border border-cs-border text-cs-muted font-mono text-xs uppercase tracking-wider px-6 py-3 hover:border-cs-ochre hover:text-cs-ochre transition-colors">
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-cs-card border border-cs-border p-6 sm:p-8 space-y-6" data-testid="contact-form">
                <h2 className="font-anton text-2xl uppercase text-cs-text mb-2 tracking-wide">Send a Message</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Name <span className="text-cs-alert">*</span></label>
                    <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className={inputClass} placeholder="Your name" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className={labelClass}>Email <span className="text-cs-alert">*</span></label>
                    <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className={inputClass} placeholder="your@email.com" data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClass} placeholder="(555) 123-4567" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label className={labelClass}>Subject <span className="text-cs-alert">*</span></label>
                    <select value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className={inputClass} data-testid="contact-subject">
                      {subjects.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Message <span className="text-cs-alert">*</span></label>
                  <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required rows={5} className={inputClass} placeholder="How can we help?" data-testid="contact-message" />
                </div>
                <button type="submit" disabled={loading} className="w-full sm:w-auto bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm px-8 py-4 hover:bg-cs-olive2 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit">
                  <Send className="w-4 h-4" />
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
